import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

interface UserProfile {
  id: number;
  email: string;
  display_name: string;
  created_at: string;
  total_tournaments: number;
  total_winnings: number;
  current_rank: number;
  total_points: number;
  win_rate: number;
}

const Profile: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    display_name: ''
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await apiService.getUserProfile();
        setProfile(data);
        setEditForm({
          display_name: data.display_name || ''
        });
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError(err instanceof Error ? err.message : 'Failed to load profile');
        
        // Fallback to mock data
        setProfile({
          id: 1,
          email: 'cricketfan@example.com',
          display_name: 'CricketFan2024',
          created_at: '2024-01-15T10:30:00Z',
          total_tournaments: 12,
          total_winnings: 8500,
          current_rank: 42,
          total_points: 2840,
          win_rate: 68.5
        });
        setEditForm({
          display_name: 'CricketFan2024'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleSaveProfile = async () => {
    try {
      const updatedProfile = await apiService.updateUserProfile(editForm);
      setProfile(updatedProfile);
      setIsEditing(false);
      alert('Profile updated successfully!');
    } catch (err) {
      console.error('Error updating profile:', err);
      alert('Failed to update profile. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({
      display_name: profile?.display_name || ''
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center'>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center'>
        <div className="text-center text-white">
          <div className="text-red-400 text-xl mb-2">⚠️</div>
          <p className="mb-4">{error}</p>
          <p className="text-sm text-blue-200">Showing sample profile data</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center'>
        <div className="text-center text-white">
          <p>No profile data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900'>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12'>
        <h1 className='text-4xl font-bold text-white mb-8'>Profile</h1>
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
          <div className='lg:col-span-1'>
            <div className='bg-white/10 backdrop-blur-md rounded-lg p-6 border border-white/20'>
              <div className='text-center mb-6'>
                <div className='w-24 h-24 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4'>
                  <span className='text-3xl text-white font-bold'>
                    {(profile.display_name || 'U').charAt(0).toUpperCase()}
                  </span>
                </div>
                <h2 className='text-xl font-semibold text-white'>{profile.display_name}</h2>
                                    <p className='text-white/60 text-sm'>@{profile.display_name}</p>
                <p className='text-white/60 text-sm'>Member since {formatDate(profile.created_at)}</p>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Email:</span>
                  <span className="text-white">{profile.email}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Total Tournaments:</span>
                  <span className="text-white font-medium">{profile.total_tournaments ?? 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Total Winnings:</span>
                  <span className="text-green-400 font-medium">₹{(profile.total_winnings ?? 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Current Rank:</span>
                  <span className="text-white font-medium">#{profile.current_rank ?? 'N/A'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Total Points:</span>
                  <span className="text-white font-medium">{profile.total_points ?? 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Win Rate:</span>
                  <span className="text-white font-medium">{profile.win_rate ?? 0}%</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className='lg:col-span-2'>
            <div className='bg-white/10 backdrop-blur-md rounded-lg p-6 border border-white/20'>
              <div className="flex justify-between items-center mb-6">
                <h3 className='text-xl font-semibold text-white'>Profile Information</h3>
                {!isEditing ? (
                  <button 
                    onClick={() => setIsEditing(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Edit Profile
                  </button>
                ) : (
                  <div className="space-x-2">
                    <button 
                      onClick={handleSaveProfile}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      Save
                    </button>
                    <button 
                      onClick={handleCancelEdit}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              
              {isEditing ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={editForm.display_name}
                      onChange={(e) => setEditForm({...editForm, display_name: e.target.value})}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter display name"
                    />
                  </div>

                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Display Name
                    </label>
                    <p className="text-white">{profile.display_name}</p>
                  </div>

                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Email
                    </label>
                    <p className="text-white">{profile.email}</p>
                  </div>
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Member Since
                    </label>
                    <p className="text-white">{formatDate(profile.created_at)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
